import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RatingDialog = ({ orderId, token, language, onClose, onSubmit }) => {
  const [restaurantRating, setRestaurantRating] = useState(0);
  const [driverRating, setDriverRating] = useState(0);
  const [restaurantComment, setRestaurantComment] = useState('');
  const [driverComment, setDriverComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/orders/${orderId}/driver-location`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d?.driver) setDriverInfo(d.driver); })
      .catch(() => {});
    return () => { alive = false; };
  }, [orderId]);

  const StarRow = ({ value, onChange, label }) => (
    <div className="mb-4">
      <p className="text-sm font-semibold text-[#004E64] mb-2">{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onClick={() => onChange(star)}
            className="transition-transform hover:scale-110"
            data-testid={`star-${label.toLowerCase().replace(/\s/g, '-')}-${star}`}
          >
            <Star
              size={32}
              className={star <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
            />
          </button>
        ))}
      </div>
    </div>
  );

  const handleSubmit = async () => {
    if (restaurantRating === 0 && driverRating === 0) {
      toast.error(language === 'pt' ? 'Escolha pelo menos uma avaliacao' : 'Please rate at least one');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ratings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderId,
          restaurant_rating: restaurantRating,
          restaurant_comment: restaurantComment,
          driver_rating: driverRating,
          driver_comment: driverComment
        })
      });
      if (res.ok) {
        toast.success(language === 'pt' ? 'Obrigado pela avaliacao!' : 'Thanks for your rating!');
        if (onSubmit) onSubmit();
        if (onClose) onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro');
      }
    } catch (err) {
      toast.error('Erro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-3xl border-[#FF6B35]/20 bg-[#FF6B35]/5">
      <CardContent className="p-6">
        <h3 className="text-base font-bold text-[#004E64] mb-4">
          {language === 'pt' ? 'Avalie o seu pedido' : 'Rate your order'}
        </h3>

        <StarRow
          value={restaurantRating}
          onChange={setRestaurantRating}
          label={language === 'pt' ? 'Restaurante' : 'Restaurant'}
        />
        {restaurantRating > 0 && (
          <input
            className="w-full p-2 rounded-xl border border-[#004E64]/10 text-sm mb-4"
            placeholder={language === 'pt' ? 'Comentario (opcional)' : 'Comment (optional)'}
            value={restaurantComment}
            onChange={e => setRestaurantComment(e.target.value)}
            data-testid="restaurant-comment-input"
          />
        )}

        {driverInfo && (
          <div className="flex items-center gap-3 mb-2 p-2 bg-white rounded-xl" data-testid="rating-driver-info">
            {driverInfo.photo_url ? (
              <img src={driverInfo.photo_url} alt={driverInfo.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#FF6B35]/40" data-testid="rating-driver-photo" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#FF6B35] text-white flex items-center justify-center font-bold">
                {driverInfo.name?.charAt(0) || 'D'}
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-[#004E64]">{driverInfo.name}</p>
              <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Entregou o seu pedido' : 'Delivered your order'}</p>
            </div>
          </div>
        )}
        <StarRow
          value={driverRating}
          onChange={setDriverRating}
          label={language === 'pt' ? 'Entregador' : 'Driver'}
        />
        {driverRating > 0 && (
          <input
            className="w-full p-2 rounded-xl border border-[#004E64]/10 text-sm mb-4"
            placeholder={language === 'pt' ? 'Comentario (opcional)' : 'Comment (optional)'}
            value={driverComment}
            onChange={e => setDriverComment(e.target.value)}
            data-testid="driver-comment-input"
          />
        )}

        <div className="flex gap-3">
          {onClose && (
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">
              {language === 'pt' ? 'Mais Tarde' : 'Later'}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
            data-testid="submit-rating-btn"
          >
            {loading ? '...' : (language === 'pt' ? 'Enviar Avaliacao' : 'Submit Rating')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RatingDialog;
